import { useState, useMemo, useEffect } from 'react';
import { saveBlob } from '@/lib/saveBlob';
import { Search, Inbox } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ServiceTabs } from '@/components/crm/ServiceTabs';
import { StatsRow } from '@/components/crm/StatsRow';
import { SearchFilters } from '@/components/crm/SearchFilters';
import { InquiryTable } from '@/components/crm/InquiryTable';
import { InquiryModal } from '@/components/crm/InquiryModal';
import { ManualEntryModal } from '@/components/crm/ManualEntryModal';
import { AnalyticsOverview } from '@/components/crm/AnalyticsOverview';
import { SummaryPanels } from '@/components/crm/SummaryPanels';
import { ActivationCard } from '@/components/dashboard/ActivationCard';
import { BetaBadge } from '@/components/shared/BetaBadge';
import StreakChip from '@/components/dashboard/StreakChip';
import SpaceshipIcon from '@/components/shared/SpaceshipIcon';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { MiniAnalyticsBar } from '@/components/dashboard/MiniAnalyticsBar';
import { ThisWeekStrip } from '@/components/dashboard/ThisWeekStrip';
import StudioRings from '@/components/dashboard/StudioRings';
import NewDashboard from '@/components/dashboard/NewDashboard';
import { useNewShell } from '@/hooks/useNewShell';
import { useGoogie } from '@/hooks/useGoogie';
import { GoogieToday } from '@/components/googie/screens/GoogieToday';
import { NovaVignette } from '@/components/nova/NovaVignette';
import { GlobalSearch, useGlobalSearch } from '@/components/dashboard/GlobalSearch';
import RecentProjects from '@/components/dashboard/RecentProjects';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonStats, SkeletonTable } from '@/components/shared/SkeletonCard';
import { Button } from '@/components/ui/button';
import { ServiceType, Inquiry, InquiryStatus, ServiceSummary } from '@/types/crm';
import { useOrganization } from '@/services/organizations';
import { useInquiryCategories } from '@/services/inquiryCategories';
import { useClients, useClientsRealtime } from '@/services/clients';
import { useProjects, useProjectsRealtime } from '@/services/projects';
import { useNovaSuggestionsWithAmbient } from '@/hooks/useNovaSuggestions';
import { NovaSuggestionCard } from '@/components/nova/NovaSuggestionCard';
import { useGoalsRealtime } from '@/services/passport';
import { PinnedGoalsCard } from '@/components/dashboard/PinnedGoalsCard';
import { ServiceTabConfig, getDefaultServiceTabs } from '@/data/serviceTabPresets';
import {
  useInquiries,
  useUpdateInquiry,
  useDeleteInquiry,
  useInquiryStats,
  useAnalyticsData,
  useServiceSummary } from
'@/services/inquiries';
import { useToast } from '@/hooks/use-toast';
// HOTFIX-2 — PostgrestError isn't an Error instance; parseSupabaseError
// is the only safe way to read a Supabase failure message.
import debug from '@/lib/debug';

const Index = () => {
  // Redesign A/B flag. When on, return early with NewDashboard
  // (see early-return below the data hooks). When off, continue
  // through the existing cream-light dashboard render.
  const [useNewShellOn] = useNewShell();
  const [googieOn] = useGoogie();
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: organization } = useOrganization();

  // Cross-device live sync — anything inserted/updated/deleted on iOS
  // or another browser tab refreshes this dashboard within a tick.
  // Nova reacts to inserts (new client → celebrate, new project → ooh).
  useClientsRealtime();
  useProjectsRealtime();
  useGoalsRealtime();

  // Live suggestions from the rules engine — top one shows above the
  // analytics strip so the studio sees what Nova noticed first.
  const { top: novaSuggestion, dismiss: dismissNovaSuggestion } = useNovaSuggestionsWithAmbient();

  // Mission Control "Today" header — matches the iOS reference
  // Title: Today
  // Subtitle: full date in lunar teal (e.g. "Wednesday, April 29")
  // Greeting: "Good morning/afternoon/evening, {firstName}."
  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const hour = today.getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const fullName: string =
    (user?.user_metadata as Record<string, string> | undefined)?.full_name || '';
  const firstName = fullName.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  const { data: inquiryCategories = [] } = useInquiryCategories();

  // Get service tabs from inquiry_categories table, fallback to org.service_tabs, then defaults
  // Always include an "All" tab first
  const serviceTabs: ServiceTabConfig[] = useMemo(() => {
    const allTab: ServiceTabConfig = { id: 'all', label: 'All Inquiries', icon: 'inbox' };

    if (inquiryCategories.length > 0) {
      return [
      allTab,
      ...inquiryCategories.map((cat) => ({
        id: cat.name.trim().toLowerCase().replace(/\s+/g, '_'),
        label: cat.name,
        icon: cat.icon || 'sparkles'
      }))];

    }
    if (organization?.service_tabs && Array.isArray(organization.service_tabs) && organization.service_tabs.length > 0) {
      return [allTab, ...(organization.service_tabs as ServiceTabConfig[])];
    }
    return [allTab, ...getDefaultServiceTabs()];
  }, [inquiryCategories, organization]);

  const [activeTab, setActiveTab] = useState<ServiceType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [portalsFilter, setPortalsFilter] = useState(false);
  const [activeStatFilter, setActiveStatFilter] = useState<InquiryStatus | 'all' | 'portals'>('all');
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);


  // Set default active tab when categories load
  useEffect(() => {
    if (serviceTabs.length > 0 && !activeTab) {
      setActiveTab('all');
    }
  }, [serviceTabs, activeTab]);

  // Fetch data with hooks
  const { data: inquiries = [], isLoading: loadingInquiries } = useInquiries();
  const { data: clients = [], isLoading: loadingClients } = useClients();
  const { data: projects = [] } = useProjects();

  // Today widgets only render once the studio has *something* to show.
  // Earlier we gated on inquiries.length only — that hides analytics
  // for studios doing manual project entry without a public form. The
  // combined gate lights up the dashboard the moment they have any
  // real activity (inquiries OR projects).
  const hasStudioActivity = inquiries.length + projects.length > 0;

  // Build a mapping from category_id to tab slug for client filtering
  const categoryIdToSlug = useMemo(() => {
    const map: Record<string, string> = {};
    inquiryCategories.forEach((cat) => {
      map[cat.id] = cat.name.trim().toLowerCase().replace(/\s+/g, '_');
    });
    return map;
  }, [inquiryCategories]);

  // Convert tagged clients to inquiry-like objects for unified display
  const clientAsInquiries = useMemo((): Inquiry[] => {
    return clients.
    filter((c) => c.category_id).
    map((c) => ({
      id: `client-${c.id}`,
      name: c.name,
      email: c.email || '',
      phone: c.phone || undefined,
      interest: inquiryCategories.find((cat) => cat.id === c.category_id)?.name?.trim() || 'Client',
      budget: '$0',
      status: 'accepted' as InquiryStatus,
      projectStage: 'lead' as any,
      clientPortalEnabled: false,
      submittedAt: (c as any).created_at || (c as any).createdAt || new Date().toISOString(),
      serviceType: c.category_id ? categoryIdToSlug[c.category_id] || 'general' : 'general'
    }));
  }, [clients, categoryIdToSlug, inquiryCategories]);

  // Mutations
  const updateInquiryMutation = useUpdateInquiry();
  const deleteInquiryMutation = useDeleteInquiry();

  // Get stats
  const stats = useInquiryStats(activeTab);
  const analytics = useAnalyticsData();

  // Merge inquiries + tagged clients
  const allEntries = useMemo(() => {
    // Deduplicate: exclude client entries whose email already exists in inquiries with same serviceType
    const inquiryEmails = new Set(inquiries.map((i) => `${i.email}|${i.serviceType}`));
    const uniqueClientEntries = clientAsInquiries.filter(
      (c) => !inquiryEmails.has(`${c.email}|${c.serviceType}`)
    );
    return [...inquiries, ...uniqueClientEntries];
  }, [inquiries, clientAsInquiries]);

  // Generate summaries for all service tabs
  const summaries = useMemo(() => {
    const result: Record<string, ServiceSummary> = {};
    serviceTabs.forEach((tab) => {
      const tabEntries = allEntries.filter((i) => i.serviceType === tab.id);
      result[tab.id] = {
        total: tabEntries.length,
        accepted: tabEntries.filter((i) => i.status === 'accepted').length,
        inProgress: tabEntries.filter((i) => i.status === 'in_progress').length
      };
    });
    return result;
  }, [allEntries, serviceTabs]);

  // Filter entries based on active tab, search, status, and portals filter
  const filteredInquiries = useMemo(() => {
    return allEntries.filter((inquiry) => {
      const matchesTab = activeTab === 'all' || inquiry.serviceType === activeTab;
      const matchesSearch =
      inquiry.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
      const matchesPortals = !portalsFilter || inquiry.clientPortalEnabled;

      return matchesTab && matchesSearch && matchesStatus && matchesPortals;
    });
  }, [allEntries, activeTab, searchQuery, statusFilter, portalsFilter]);

  // Handle stat card clicks - updates both the status filter and portals filter
  const handleStatClick = (filter: InquiryStatus | 'all' | 'portals') => {
    setActiveStatFilter(filter);

    if (filter === 'portals') {
      setStatusFilter('all');
      setPortalsFilter(true);
    } else if (filter === 'all') {
      setStatusFilter('all');
      setPortalsFilter(false);
    } else {
      setStatusFilter(filter);
      setPortalsFilter(false);
    }
  };

  const handleManualEntry = () => {
    setShowManualEntry(true);
  };

  const handleExportCSV = () => {
    // Generate CSV from filtered inquiries
    const headers = ['Name', 'Email', 'Phone', 'Interest', 'Status', 'Service Type', 'Submitted'];
    const rows = filteredInquiries.map((i) => [
    i.name,
    i.email,
    i.phone || '',
    i.interest,
    i.status,
    i.serviceType,
    i.submittedAt]
    );

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    // DL-SAFARI-1 — see saveBlob: revoking in the click's own tick made
    // Safari save nothing, silently.
    const blob = new Blob([csv], { type: 'text/csv' });
    saveBlob(blob, `inquiries-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`);

    toast({
      title: 'Export Complete',
      description: 'Your CSV file has been downloaded.'
    });
  };

  // This callback is triggered after InquiryModal saves successfully
  // The modal already handles the mutation, so we just close it here
  const handleUpdateInquiry = async (_updated: Inquiry) => {
    setSelectedInquiry(null);
    // No need to call mutation again - InquiryModal already saved
  };

  const handleDeleteInquiry = async (id: string) => {
    try {
      await deleteInquiryMutation.mutateAsync(id);
      setSelectedInquiry(null);
      toast({
        title: 'Inquiry Deleted',
        description: 'The inquiry has been removed.',
        variant: 'destructive'
      });
    } catch (error) {
      // HOTFIX-2 — name the real reason. Deleting a tagged-client row
      // (`client-<uuid>`) is rejected up in the service with a
      // readable message; a bare "Failed to delete inquiry." hid it.
      console.error('Delete inquiry failed:', error);
      toast({
        title: 'Error',
        description: `Failed to delete — ${debug.parseSupabaseError(error)}`,
        variant: 'destructive'
      });
    }
  };

  const globalSearch = useGlobalSearch();

  // GOOGIE-1 — the redesign outranks both older looks. Its Today is
  // self-contained (own shell, own nav) and reads the same hooks, so
  // nothing here re-fetches.
  if (googieOn) {
    return <GoogieToday />;
  }

  // Web redesign Pass 2 (#320) — when the new shell is on, render
  // the redesigned dark dashboard instead of the existing cream
  // layout below. NewDashboard is self-contained and uses the same
  // data hooks so we don't duplicate fetches.
  if (useNewShellOn) {
    return <NewDashboard />;
  }

  return (
    <main className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="space-y-8">
        {/* Editorial hero — canonical page shape (eyebrow + Fraunces italic H1
            + muted subtitle). The date sits above the title as the eyebrow,
            greeting sits below as the subtitle. Badges dock to the right so
            they don't compete with the hero on the left. */}
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="orbit-eyebrow">{dateLabel}</p>
            <h1 className="font-editorial text-4xl md:text-5xl font-normal text-foreground tracking-tighter leading-none">
              Today
            </h1>
            <p className="text-muted-foreground mt-2">
              Good {timeOfDay}, {firstName}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BetaBadge />
            <StreakChip />
          </div>
        </header>

        {/* Quick actions sit alone, no competing search chrome. */}
        <div>
          <QuickActions />
        </div>

        {/* Operations hero — what's happening THIS week. Today's shoots,
            deadlines, idle leads, active projects. Each tile links to
            the relevant page so the studio can act in one tap. Auto-
            hides for fresh accounts with no projects + no inquiries. */}
        <ThisWeekStrip />

        {/* Apple-Watch-style activity rings — bookings / pipeline /
            revenue against weekly goals. The motivation layer above
            the raw stats. Hidden for fresh accounts so a brand-new
            studio's first impression isn't three empty rings. */}
        {hasStudioActivity && <StudioRings />}

        {/* Pinned goals — opt-in widget. Hides itself when nothing's pinned. */}
        <PinnedGoalsCard />

        {/* Nova noticed — top suggestion from the live rules engine.
            When there's nothing actionable to surface, instead of
            hiding her entirely we show a small workstation vignette
            so studios know she's still around, just quietly working.
            Only shows when the studio has any activity at all (avoids
            stacking with the Getting Started guide for fresh accounts). */}
        {novaSuggestion ? (
          <NovaSuggestionCard
            suggestion={novaSuggestion}
            onDismiss={dismissNovaSuggestion}
          />
        ) : hasStudioActivity ? (
          <div className="rounded-2xl border border-border/40 bg-card/40 px-5 py-4 flex items-center gap-4">
            <div className="shrink-0">
              <NovaVignette variant="workstation" size={72} />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="text-foreground font-medium leading-snug">Nothing urgent on Nova's radar.</p>
              <p className="text-xs mt-0.5">
                She's at her workstation, taking notes. Drop into chat anytime.
              </p>
            </div>
          </div>
        ) : null}

        {/* Today's snapshot — ambient stat strip. Anchored by a
            quiet section heading so the row reads as one
            intentional band rather than a floating chip. */}
        {hasStudioActivity && (
          <section className="space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="section-eyebrow">Snapshot</p>
                <h2 className="font-editorial text-2xl md:text-3xl text-foreground leading-none">Today at a glance</h2>
              </div>
            </div>
            <MiniAnalyticsBar />
          </section>
        )}

        {/* Studio Setup activation arc */}
        <ActivationCard />

        {/* ───────────────────────────────────────────────────────────
            Section break — everything above is the "at a glance"
            dashboard (Today, This Week, Goals, Nova, mini analytics).
            Everything below is the working surface for handling
            inquiries + pipeline. Task #25 was about giving the bottom
            half its own visual home so it feels like below-the-fold
            work rather than one continuous wall of widgets.
            ─────────────────────────────────────────────────────────── */}
        {hasStudioActivity && (
          <div className="relative pt-4">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <div className="pt-8">
              <p className="section-eyebrow">Working surface</p>
              <h2 className="font-editorial text-3xl md:text-4xl text-foreground leading-none">
                Inquiries &amp; pipeline
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Filter, search, and action your leads. Everything below scrolls.
              </p>
            </div>
          </div>
        )}

        {/* Service Tabs */}
        <ServiceTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Global Search Modal */}
        <GlobalSearch open={globalSearch.open} onOpenChange={globalSearch.setOpen} />

        {/* Pipeline — hero metric tiles. Filter chips with a real
            sense of weight; tapping any card filters the table. */}
        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="section-eyebrow">Pipeline</p>
              <h2 className="font-editorial text-2xl md:text-3xl text-foreground leading-none">Inquiries</h2>
            </div>
            <p className="text-sm text-muted-foreground hidden md:block">
              Tap a tile to filter the list below.
            </p>
          </div>
          {loadingInquiries ?
          <SkeletonStats count={5} /> :

          <StatsRow
            stats={stats}
            activeFilter={activeStatFilter}
            onStatClick={handleStatClick} />

          }
        </section>

        {/* Filters */}
        <SearchFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onManualEntry={handleManualEntry}
          onExportCSV={handleExportCSV} />
        

        {/* Inquiry Table */}
        {loadingInquiries ?
        <SkeletonTable rows={5} /> :
        filteredInquiries.length === 0 && inquiries.length === 0 ?
        <EmptyState
          icon={Inbox}
          title="No inquiries yet"
          description="Every lead lands here. When you forget to follow up, the shoot dies quietly — share your form or add one by hand."
          actionLabel="Add a lead"
          onAction={handleManualEntry} /> :


        <InquiryTable
          inquiries={filteredInquiries}
          onViewDetails={setSelectedInquiry} />

        }

        {/* Data-driven Today widgets — only render once the studio has
            something meaningful to show. A brand-new account with zero
            inquiries should see the Getting Started guide above and a
            clean canvas, not an empty AnalyticsOverview / RecentProjects
            / CreatorAccountsSection rendering "0 / 0 / 0" everywhere.
            Once the org has its first inquiry the full dashboard turns on. */}
        {hasStudioActivity && (
          <>
            {/* Recent Projects */}
            <RecentProjects />

            {/* Analytics Overview */}
            <AnalyticsOverview analytics={analytics} />

            {/* Summary Panels */}
            <SummaryPanels summaries={summaries} />
          </>
        )}
      </div>

      {/* Inquiry Modal */}
      <InquiryModal
        inquiry={selectedInquiry}
        open={!!selectedInquiry}
        onOpenChange={(open) => !open && setSelectedInquiry(null)}
        onUpdate={handleUpdateInquiry}
        onDelete={handleDeleteInquiry} />
      

      {/* Manual Entry Modal */}
      {showManualEntry &&
      <ManualEntryModal
        onClose={() => setShowManualEntry(false)}
        defaultServiceType={activeTab} />

      }
      
    </main>);

};

export default Index;